import { useChat } from '@ai-sdk/react';
import {
  type AgentV2ChatThreadRow,
  createAgentV2ThreadApi,
  prepareAgentV2StreamSendMessagesRequest,
  resolveAgentV2HttpBaseUrl,
  shouldCaptureAgentChatStreamError,
  useAgentPersistedThreadId,
  useAgentStreamIdleThreadReady,
  useAgentV2ThreadHistory,
} from '@kintsugi/advisor-ui';
import * as Sentry from '@sentry/react';
import { DefaultChatTransport } from 'ai';
import type { AdminAgentScope } from '@/components/admin-advisor/admin-agent-contexts';
import { getApiToken } from '@/utils/api';
import { useCallback, useMemo, useRef } from 'react';
import type { UIMessage } from 'ai';

const AGENT_V2_CHAT_STREAM_PATH = '/v2/chat/stream';

/** ``use_case`` for admin panel lane (matches ``GET /v2/chat/use-cases``). */
export const ADMIN_USE_CASE = 'admin';

/** Stable fetch identity — avoid inline ``fetch.bind`` which breaks history effect memoization. */
const adminAgentFetch: typeof globalThis.fetch = globalThis.fetch.bind(globalThis);

function adminAgentHttpBase(): string {
  return resolveAgentV2HttpBaseUrl(import.meta.env.VITE_API_URL).replace(/\/$/, '');
}

function adminAgentThreadApi(organizationId?: string) {
  return createAgentV2ThreadApi({
    baseUrl: adminAgentHttpBase(),
    useCase: ADMIN_USE_CASE,
    fetch: adminAgentFetch,
    buildHeaders: () => buildAdminAgentAuthHeaders(organizationId),
  });
}

export async function buildAdminAgentAuthHeaders(organizationId?: string): Promise<Record<string, string>> {
  const h: Record<string, string> = {};
  const token = getApiToken();
  if (token) {
    h['Authorization'] = `Bearer ${token}`;
  }
  if (organizationId) {
    h['x-organization-id'] = organizationId;
  }
  return h;
}

export async function fetchAdminAgentThreadList(options?: {
  organizationId?: string;
  limit?: number;
  offset?: number;
}): Promise<AgentV2ChatThreadRow[]> {
  return adminAgentThreadApi(options?.organizationId).listThreads({
    limit: options?.limit,
    offset: options?.offset,
  });
}

export async function deleteAdminAgentThread(
  threadId: string,
  options?: { organizationId?: string; useCase?: string }
): Promise<void> {
  return adminAgentThreadApi(options?.organizationId).deleteThread(threadId, {
    useCase: options?.useCase,
  });
}

export async function fetchAdminAgentThreadMessages(
  threadId: string,
  options?: { organizationId?: string; limit?: number; offset?: number; useCase?: string }
) {
  return adminAgentThreadApi(options?.organizationId).fetchMessages(threadId, {
    limit: options?.limit,
    offset: options?.offset,
    useCase: options?.useCase,
  });
}

export function useAdminAgentV2ThreadHistory(options: {
  enabled: boolean;
  organizationId?: string;
  formatError: (raw: string) => string;
}) {
  const baseUrl = useMemo(() => resolveAgentV2HttpBaseUrl(import.meta.env.VITE_API_URL).replace(/\/$/, ''), []);
  const orgIdRef = useRef(options.organizationId);
  orgIdRef.current = options.organizationId;
  const buildHeaders = useCallback(() => buildAdminAgentAuthHeaders(orgIdRef.current), []);
  const listQueryKey = options.organizationId ?? '';

  return useAgentV2ThreadHistory({
    enabled: options.enabled,
    listQueryKey,
    useCase: ADMIN_USE_CASE,
    baseUrl,
    fetch: adminAgentFetch,
    buildHeaders,
    formatError: options.formatError,
  });
}

export type AdminAgentPageContext = Record<string, string | number | boolean | null | undefined>;

export type AdminAgentChatInput = {
  chatId: string;
  scope: AdminAgentScope;
  organizationId?: string;
  organizationName?: string;
  pathname: string;
  onChatError?: (error: Error) => void;
};

export function buildContextPayload(
  scope: AdminAgentScope,
  pathname: string,
  organizationId: string | undefined,
  organizationName: string | undefined
): AdminAgentPageContext {
  if (scope === 'portfolio') {
    return { scope: 'portfolio', pathname, clientSurface: 'admin' };
  }
  return {
    scope: 'organization',
    pathname,
    clientSurface: 'admin',
    clientOrganizationId: organizationId ?? null,
    clientOrganizationName: organizationName ?? null,
  };
}

export function useAdminAgentChat({
  chatId,
  scope,
  organizationId,
  organizationName,
  pathname,
  onChatError,
}: AdminAgentChatInput) {
  const {
    threadId: agentThreadId,
    threadIdRef: agentThreadIdRef,
    threadServerReady,
    markThreadServerReady,
    clearThreadServerReady,
    beginNewAgentThread,
    selectPersistedThread,
  } = useAgentPersistedThreadId(chatId);

  const contextPayload = useMemo(
    () => buildContextPayload(scope, pathname, organizationId, organizationName),
    [scope, pathname, organizationId, organizationName]
  );

  const contextRef = useRef(contextPayload);
  contextRef.current = contextPayload;

  const scopedOrgIdRef = useRef<string | undefined>(undefined);
  scopedOrgIdRef.current = scope === 'organization' ? organizationId : undefined;

  const nextStreamRouteUseCaseRef = useRef<string | undefined>(undefined);

  const queueRouteUseCaseForNextStream = useCallback((lane: string | undefined) => {
    const normalized = lane?.trim();
    nextStreamRouteUseCaseRef.current = normalized && normalized.length > 0 ? normalized : undefined;
  }, []);

  const streamBase = useMemo(() => resolveAgentV2HttpBaseUrl(import.meta.env.VITE_API_URL).replace(/\/$/, ''), []);
  const api = `${streamBase}${AGENT_V2_CHAT_STREAM_PATH}`;

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api,
        fetch: adminAgentFetch,
        prepareSendMessagesRequest: prepareAgentV2StreamSendMessagesRequest,
        headers: async () => {
          const h: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          const token = getApiToken();
          if (token) h['Authorization'] = `Bearer ${token}`;
          const orgId = scopedOrgIdRef.current;
          if (orgId) h['x-organization-id'] = orgId;
          return h;
        },
        body: () => {
          const route = nextStreamRouteUseCaseRef.current;
          nextStreamRouteUseCaseRef.current = undefined;
          const payload: Record<string, unknown> = {
            use_case: ADMIN_USE_CASE,
            context: contextRef.current,
            thread_id: agentThreadIdRef.current,
          };
          if (route) {
            payload.route_use_case = route;
          }
          return payload;
        },
      }),
    [api, agentThreadIdRef]
  );

  const submitMessageFeedback = useCallback(
    async (messageId: string, rating: 'up' | 'down' | null, options?: { comment?: string | null }) => {
      await adminAgentThreadApi(scopedOrgIdRef.current).submitFeedback({
        messageId,
        rating,
        comment: options?.comment,
      });
    },
    []
  );

  const onChatErrorRef = useRef(onChatError);
  onChatErrorRef.current = onChatError;

  const chat = useChat({
    id: chatId,
    transport,
    onError: error => {
      const msg = error.message?.trim() ?? '';
      let requestId: string | undefined;
      if (msg.startsWith('{')) {
        try {
          requestId = (JSON.parse(msg) as { request_id?: string }).request_id;
        } catch {
          /* ignore */
        }
      }
      if (requestId) {
        console.error('[AdminAgent] chat error:', error, { request_id: requestId });
      } else {
        console.error('[AdminAgent] chat error:', error);
      }
      if (shouldCaptureAgentChatStreamError(error)) {
        Sentry.captureException(error, {
          tags: { component: 'admin_agent_chat' },
          extra: requestId ? { request_id: requestId } : {},
        });
      }
      onChatErrorRef.current?.(error);
    },
    experimental_throttle: 50,
  });

  const streamIdleToken = useAgentStreamIdleThreadReady({
    chatStatus: chat.status,
    getThreadId: () => agentThreadIdRef.current,
    markThreadServerReady,
  });

  return {
    ...chat,
    agentThreadId,
    agentThreadIdRef,
    threadServerReady,
    clearThreadServerReady,
    streamIdleToken,
    queueRouteUseCaseForNextStream,
    beginNewAgentThread,
    selectPersistedThread,
    submitMessageFeedback,
  };
}

export function getMessageDisplayText(message: UIMessage): string {
  if (message.parts?.length) {
    return message.parts
      .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
      .map(p => p.text)
      .join('');
  }
  return '';
}
